import { useState } from 'react';
import { apiListOffers, apiUpsertOffer } from '~/lib/api/payments';
import { classNames } from '~/utils/classNames';
import {
  apiErrorMessage,
  formatMinor,
  isValidCurrency,
  majorToMinor,
  OFFER_SLUG_RE,
  recurringLabel,
  slugify,
} from './payments-model';
import { useResource } from './use-resource';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  CopyField,
  ErrorBanner,
  Field,
  LoadError,
  Section,
  SkeletonCard,
  Spinner,
} from './ui';

type Billing = 'once' | 'month' | 'year';

export function OffersSection({ appId, currency }: { appId: string; currency: string | null }) {
  const offers = useResource(() => apiListOffers(appId), appId);
  const [creating, setCreating] = useState(false);
  const list = (offers.data ?? []).filter((o) => o.status === 'active');

  const addButton = !creating && (
    <button onClick={() => setCreating(true)} className={BTN_GHOST}>
      <div className="i-ph:plus text-[13px]" />
      New
    </button>
  );

  return (
    <Section title="Buy buttons" icon="i-ph:cursor-click" aside={addButton}>
      <p className="text-[12px] leading-relaxed text-muted">
        A priced button you can drop on any page. Ask the agent to add one, or place it yourself with{' '}
        <code className="font-geist-mono text-[11px] text-secondary">{'<BuyButton offer="slug" />'}</code>.
      </p>

      {creating && (
        <OfferForm
          appId={appId}
          defaultCurrency={currency ?? ''}
          onDone={(saved) => {
            setCreating(false);

            if (saved) {
              void offers.reload();
            }
          }}
        />
      )}

      {offers.loading && !offers.data ? (
        <SkeletonCard />
      ) : offers.error ? (
        <LoadError message="Could not load buy buttons." onRetry={() => void offers.reload()} />
      ) : list.length === 0 ? (
        !creating && (
          <div className={classNames(CARD, 'px-4 py-5 text-center')}>
            <p className="text-[12px] text-muted">No buy buttons yet.</p>
          </div>
        )
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {list.map((offer) => (
            <div key={offer.id} className="space-y-2 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium text-primary">{offer.title}</div>
                  <div className="font-geist-mono text-[11px] text-muted">{offer.slug}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-geist-mono text-[13px] text-primary">
                    {formatMinor(offer.amount, offer.currency)}
                  </div>
                  <div className="text-[11px] text-muted">{recurringLabel(offer.recurring)}</div>
                </div>
              </div>
              <CopyField value={`<BuyButton offer="${offer.slug}" />`} />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function OfferForm({
  appId,
  defaultCurrency,
  onDone,
}: {
  appId: string;
  defaultCurrency: string;
  onDone: (saved: boolean) => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [billing, setBilling] = useState<Billing>('once');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(title);
  const code = currency.trim().toUpperCase();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Give the button a title.');
      return;
    }

    if (!OFFER_SLUG_RE.test(effectiveSlug)) {
      setError('The slug can only use lowercase letters, numbers and dashes.');
      return;
    }

    if (!isValidCurrency(code)) {
      setError('Use a three-letter currency code, e.g. INR or USD.');
      return;
    }

    const minor = majorToMinor(amount, code);

    if (minor === null) {
      setError('Enter a price above zero.');
      return;
    }

    setSaving(true);

    try {
      await apiUpsertOffer(appId, {
        slug: effectiveSlug,
        title: title.trim(),
        amount: minor,
        currency: code,
        recurring: billing === 'once' ? null : { interval: billing },
      });
      onDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this buy button.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
      <Field label="Title">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="1:1 coaching call"
          className="ui-input"
        />
      </Field>

      <Field label="Slug" hint="Used in the code. Saving an existing slug updates that button.">
        <input
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value.toLowerCase());
          }}
          placeholder="coaching-call"
          spellCheck={false}
          className="ui-input-mono"
        />
      </Field>

      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Field label="Price">
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="999"
              className="ui-input-mono"
            />
          </Field>
        </div>
        <div className="w-20">
          <Field label="Currency">
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
              maxLength={3}
              spellCheck={false}
              className="ui-input-mono uppercase"
            />
          </Field>
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="block text-[11px] text-secondary">Billing</span>
        <div className="ui-segmented">
          {(
            [
              ['once', 'One-time'],
              ['month', 'Monthly'],
              ['year', 'Yearly'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setBilling(value)}
              className={classNames(
                'ui-segmented-btn !px-3 !py-1 !text-[11px]',
                billing === value && 'ui-segmented-btn-active',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className={BTN_SECONDARY}>
          {saving && <Spinner />}
          {saving ? 'Saving…' : 'Save buy button'}
        </button>
        <button type="button" onClick={() => onDone(false)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
